import { BookingResponseDto } from "../../api/bookingApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import {
  AlertCircle,
  Calendar,
  Check,
  Clock,
  Euro,
  MapPin,
} from "lucide-react";
import { Link } from "react-router";
import { ProfilePicture } from "../profile/ProfilePicture.tsx";
import {
  formatSmartDate,
  formatTimeRange,
  getFutureTimeSlots,
} from "./formatBooking.ts";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";

interface PendingBookingCardProps {
  booking: BookingResponseDto;
  onAccept: () => void;
  isAccepting: boolean;
}

export default function PendingBookingCard({
  booking,
  onAccept,
  isAccepting,
}: PendingBookingCardProps) {
  const { language, dateFnsLocale } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();

  const InstrumentIcon = getInstrumentIcon(booking.instrument);
  const instrumentLabel = getTranslations(
    supportedInstruments,
    booking.instrument,
    language,
  );

  const futureSlots = getFutureTimeSlots(booking.timeSlots);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-[--shadow-card]">
      {/* Header: status + instrument */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <InstrumentIcon className="h-5 w-5 shrink-0 text-amber-700" />
          <span className="truncate text-sm font-semibold text-amber-900">
            {instrumentLabel}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          <AlertCircle className="h-3 w-3" />
          <Trans>Pending</Trans>
        </div>
      </div>

      {/* Student info + location */}
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 shrink-0">
          <ProfilePicture
            profilePictureUrl={booking.bookerProfilePictureUrl}
            className="h-9 w-9"
            iconSize={20}
          />
        </div>
        <div className="flex min-w-0 flex-col">
          <Link
            to={`/profile/${booking.bookerId}`}
            className="truncate text-sm font-medium text-amber-900 hover:underline"
          >
            {booking.bookerName}
          </Link>
          <span className="flex items-center gap-1 truncate text-xs text-amber-700">
            <MapPin className="h-3 w-3 shrink-0" />
            {booking.locationName}
          </span>
        </div>
      </div>

      {/* Timeslots with date + time */}
      {futureSlots.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {futureSlots.map((slot, index) => {
            const dateLabel = formatSmartDate(
              slot.from,
              dateFnsLocale,
              t`Today`,
              t`Tomorrow`,
            );
            const timeRange = formatTimeRange(slot, dateFnsLocale);

            return (
              <div
                key={index}
                className="flex items-center gap-3 text-xs font-medium text-amber-900"
              >
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span>{dateLabel}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span>{timeRange}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Price */}
      {booking.price != null && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
          <Euro className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>€{booking.price.toFixed(2)}</span>
        </div>
      )}

      {/* Accept action */}
      <button
        onClick={onAccept}
        disabled={isAccepting}
        className="mt-1 flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-green-600 px-3 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-150 hover:bg-green-700 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100"
      >
        <Check className="h-4 w-4" />
        <span>
          {isAccepting ? (
            <Trans>Accepting…</Trans>
          ) : (
            <Trans>Accept Booking</Trans>
          )}
        </span>
      </button>
    </div>
  );
}
